<script setup lang="ts">
import { nextTick, onUnmounted, PropType, ref, useId, watch } from "vue";
import { SheetController } from "./controller";

const props = defineProps({
  controller: {
    type: Object as PropType<SheetController>,
    required: false,
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  width: {
    type: String,
    default: undefined,
  },
  maxWidth: {
    type: String,
    default: "90%",
  },
  delay: {
    type: Number,
    default: undefined,
  },
  position: {
    type: String as PropType<"left" | "right" | "top" | "bottom">,
    default: "right",
  },
  height: {
    type: String,
    default: "100%",
  },
  maxHeight: {
    type: String,
    default: "100vh",
  },
  transparent: {
    type: Boolean,
    default: false,
  },
  zIndexOverride: {
    type: Number,
    default: undefined,
  },
  includeContentPadding: {
    type: Boolean,
    default: true,
  },
});

const isOpen = ref(false);
const titleId = `sheet-title-${useId()}`;
const showOverlay = ref(false);
const contentKey = ref(0);
const currentWidth = ref<string | undefined>(props.width);
const currentMaxWidth = ref<string | undefined>(props.maxWidth);

// Ref for the sheet content for focus management
const sheetContentRef = ref<HTMLElement | null>(null);

const emit = defineEmits(["onBeforeClose", "onClose"]);

let openTimeout: ReturnType<typeof setTimeout> | null = null;

// No global lock needed; each sheet manages its own state

const openArguments = ref<{ [key: string]: any } | undefined>(undefined);

const openSheet = (options?: { [key: string]: any }) => {
  openArguments.value = options;
  contentKey.value += 1; // bump key to force remount
  currentWidth.value = props.width;
  currentMaxWidth.value = props.maxWidth;
  if (typeof props.delay === "number" && props.delay > 0) {
    openTimeout = setTimeout(() => {
      showOverlay.value = true;
      isOpen.value = true;
      nextTick(() => {
        focusSheet();
        addKeydownListener();
      });
    }, props.delay);
  } else {
    showOverlay.value = true;
    isOpen.value = true;
    nextTick(() => {
      focusSheet();
      addKeydownListener();
    });
  }
};

onUnmounted(() => {
  if (openTimeout) {
    clearTimeout(openTimeout);
    openTimeout = null;
  }
  removeKeydownListener();
  // No global lock to release
});

const closeResolver = ref<(() => void) | null>(null);

const closeSheet = () => {
  removeKeydownListener();
  return new Promise<void>((resolve) => {
    emit("onBeforeClose");
    isOpen.value = false; // Start the slide-out
    closeResolver.value = resolve; // Only resolve after transition
  });
};

const handleOverlayClick = () => {
  closeSheet();
};

const setWidth = (width?: string) => {
  currentWidth.value = width;
};

const setMaxWidth = (maxWidth?: string) => {
  currentMaxWidth.value = maxWidth;
};

const resetSize = () => {
  currentWidth.value = props.width;
  currentMaxWidth.value = props.maxWidth;
};

const onSheetLeave = () => {
  showOverlay.value = false; // Fade out overlay after sheet slides out
  emit("onClose"); // Only emit after transition
  if (closeResolver.value) {
    closeResolver.value();
    closeResolver.value = null;
  }
};

// Bind controller methods if provided
if (props.controller) {
  props.controller.open = openSheet;
  props.controller.close = closeSheet;
  props.controller.setWidth = setWidth;
  props.controller.setMaxWidth = setMaxWidth;
  props.controller.resetSize = resetSize;
}

watch(
  () => props.width,
  (width) => {
    if (isOpen.value != true) {
      currentWidth.value = width;
    }
  }
);

watch(
  () => props.maxWidth,
  (maxWidth) => {
    if (isOpen.value != true) {
      currentMaxWidth.value = maxWidth;
    }
  }
);
// --- Focus and Escape Key Handling ---

function focusSheet() {
  if (sheetContentRef.value) {
    sheetContentRef.value.focus();
  }
}

function isFormElement(el: Element | null) {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === "input" ||
    tag === "select" ||
    tag === "textarea" ||
    el.hasAttribute("data-esc-handled")
  );
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    // Only close if focus is not in a form element inside the sheet
    const active = document.activeElement;

    // Check if the active component class containse base-sheet-content
    if (active?.classList.contains("base-sheet-content") != true) return;

    if (!sheetContentRef.value) return;
    if (active && sheetContentRef.value.contains(active)) {
      if (isFormElement(active)) {
        // Let form element handle Escape
        return;
      }
      // If focus is on sheet or a non-form element inside, close
      e.preventDefault();
      closeSheet();
    } else {
      // Focus is outside sheet, close anyway
      e.preventDefault();
      closeSheet();
    }
  }
}

function addKeydownListener() {
  document.addEventListener("keydown", onKeydown);
}
function removeKeydownListener() {
  document.removeEventListener("keydown", onKeydown);
}

// If focus leaves all children, refocus the sheet
function onSheetBlur(e: FocusEvent) {
  // Timeout needed to allow activeElement to update
  setTimeout(() => {
    if (!sheetContentRef.value) return;
    const active = document.activeElement;
    if (!active || !sheetContentRef.value.contains(active)) {
      focusSheet();
    }
  }, 0);
}
</script>

<template>
  <div>
    <!-- Trigger -->
    <span @click.stop.prevent="openSheet" style="display: contents">
      <slot name="trigger" />
    </span>

    <!-- Overlay and Sheet Content (teleported to <body>) -->
    <teleport to="body">
      <transition name="fade" appear>
        <div
          v-if="showOverlay"
          class="base-sheet-overlay z-[50]"
          :class="{
            'justify-start': position === 'left',
            'justify-end': position === 'right',
            'justify-center': position === 'top',
            'justify-center items-end': position === 'bottom',
            transparent: transparent,
          }"
          :style="{
            zIndex: zIndexOverride,
          }"
          @click.self.stop="handleOverlayClick"
        >
          <transition
            :name="
              position === 'left'
                ? 'slide-sheet-left'
                : position === 'right'
                ? 'slide-sheet-right'
                : position === 'top'
                ? 'slide-sheet-top'
                : 'slide-sheet-bottom'
            "
            appear
            @after-leave="onSheetLeave"
          >
            <div
              class="base-sheet-content z-[51] flex flex-col focus:outline-none"
              :class="{
                'rounded-tl rounded-bl': position === 'right',
                'rounded-tr rounded-br': position === 'left',
                'rounded-tl rounded-tr': position === 'bottom',
              }"
              v-if="isOpen"
              @click.stop
              :style="{
                width: currentWidth,
                maxWidth: currentMaxWidth,
                height: height,
                maxHeight: maxHeight,
              }"
              ref="sheetContentRef"
              role="dialog"
              aria-modal="true"
              :aria-labelledby="titleId"
              tabindex="-1"
              @blur="onSheetBlur"
            >
              <div
                class="base-sheet-header flex w-full flex-row items-center justify-between border-b p-4"
              >
                <div :id="titleId" class="base-sheet-title text-h5">
                  <slot name="title">
                    <div></div>
                  </slot>
                </div>
                <BaseIconButton v-if="showClose" @click="closeSheet" :size="35">
                  <BaseIconClose />
                </BaseIconButton>
              </div>
              <div
                v-if="isOpen"
                :key="contentKey"
                class="grow flex flex-col min-h-0 overflow-auto"
                :class="{ 'pt-4': includeContentPadding }"
              >
                <slot name="content" :open-arguments="openArguments" />
              </div>
              <div v-if="$slots.footer" class="p-4 border-t">
                <slot name="footer" />
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<style scoped>
.base-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: stretch;
}

.base-sheet-overlay.transparent {
  background: transparent;
  pointer-events: none;
}

.base-sheet-overlay.transparent .base-sheet-content {
  pointer-events: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.slide-sheet-right-enter-active,
.slide-sheet-right-leave-active,
.slide-sheet-left-enter-active,
.slide-sheet-left-leave-active,
.slide-sheet-top-enter-active,
.slide-sheet-top-leave-active,
.slide-sheet-bottom-enter-active,
.slide-sheet-bottom-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-sheet-right-enter-from,
.slide-sheet-right-leave-to {
  transform: translateX(100%);
}

.slide-sheet-left-enter-from,
.slide-sheet-left-leave-to {
  transform: translateX(-100%);
}

.slide-sheet-top-enter-from,
.slide-sheet-top-leave-to {
  transform: translateY(-100%);
}

.slide-sheet-bottom-enter-from,
.slide-sheet-bottom-leave-to {
  transform: translateY(100%);
}

.slide-sheet-right-enter-to,
.slide-sheet-right-leave-from,
.slide-sheet-left-enter-to,
.slide-sheet-left-leave-from,
.slide-sheet-top-enter-to,
.slide-sheet-top-leave-from,
.slide-sheet-bottom-enter-to,
.slide-sheet-bottom-leave-from {
  transform: translateX(0);
}

.slide-sheet-top-enter-to,
.slide-sheet-top-leave-from,
.slide-sheet-bottom-enter-to,
.slide-sheet-bottom-leave-from {
  transform: translateY(0);
}

.base-sheet-content {
  background: white;
  box-shadow: 0 0 24px rgba(0, 0, 0, 0.12);
  position: relative;
  display: flex;
  flex-direction: column;
  transition:
    width 0.2s ease,
    max-width 0.2s ease,
    transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Height handling for different positions */
.base-sheet-content:not([style*="height"]) {
  height: 100vh;
}

/* For bottom and top positions, use maxHeight when height is not specified */
.base-sheet-content[data-position="bottom"]:not([style*="height"]),
.base-sheet-content[data-position="top"]:not([style*="height"]) {
  height: auto;
  max-height: 90vh;
}

.base-sheet-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #444;
  padding: 0.25rem 0.5rem;
  margin: 0;
  transition: background 0.15s;
  border-radius: 0.25rem;
}
.base-sheet-close:hover {
  background: #f2f2f2;
}
</style>
